import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import senderImg from "../assets/traveller.png";
import receiverImg from "../assets/guide.png";
import {
  connectSocket,
  sendMessage,
  disconnectSocket,
} from "../services/socketService";
import { jwtDecode } from "jwt-decode";
import reviewService from "../services/reviewService";
import tourReviewService from "../services/tourReviewService";
import { Button } from "antd";
import "./Message2.css";
import { getApiBaseUrl } from "../utils/apiConfig";

function Chat() {
  const API_URL = getApiBaseUrl();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    bookingId,
    isClosed,
    participant,
    contextType,
    contextTitle,
    checkoutDate,
    bookingDate,
  } = location.state || {};

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [reviewExists, setReviewExists] = useState(false);
  const [isLoadingReview, setIsLoadingReview] = useState(true);
  const [tourReviewExists, setTourReviewExists] = useState(false);
  const [isLoadingTourReview, setIsLoadingTourReview] = useState(true);

  useEffect(() => {
  const token = localStorage.getItem("jwt_token");
  if (!token) return;

  const decoded = jwtDecode(token);
  setCurrentUserId(decoded.id);

  connectSocket(token, (message) => {
    if (message.sender.id !== decoded.id) {
      setMessages((prev) => [...prev, message]);
    }
  });

  const checkHotel = async () => {
    if (contextType !== "booking") return;
    try {
      const exists = await reviewService.checkReviewExists(bookingId);
      setReviewExists(exists);
    } catch (error) {
      console.error("Error checking hotel review:", error);
    } finally {
      setIsLoadingReview(false);
    }
  };

  const checkTour = async () => {
    if (contextType !== "tour_booking") return;
    try {
      const exists = await tourReviewService.checkTourReviewExists(bookingId);
      setTourReviewExists(exists);
    } catch (error) {
      console.error("Error checking tour review:", error);
    } finally {
      setIsLoadingTourReview(false);
    }
  };

  checkHotel();
  checkTour();

  return () => disconnectSocket();
}, [contextType, bookingId]);

  useEffect(() => {
    // Fetch initial messages from API
    const fetchMessages = async () => {
      try {
        const token = localStorage.getItem("jwt_token");
        const convId = location.pathname.split("/").pop();
        const response = await fetch(
          `${API_URL}/conversations/${convId}/messages`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await response.json();
        setMessages(data);
      } catch (err) {
        console.error("Error fetching chat messages:", err);
      }
    };

    fetchMessages();
  }, [location.pathname, API_URL]);

  const handleSend = () => {
     if (isClosed) return; 
    if (!input.trim() || !currentUserId) return;

    const newMessage = {
      sender: { id: currentUserId, full_name: "You" },
      message_text: input,
      sent_at: new Date().toISOString(),
    };

    // Optimistic update
    setMessages((prev) => [...prev, newMessage]);

    const convId = location.pathname.split("/").pop();
    sendMessage(convId, input);

    setInput("");
  };

  const renderHotelReviewButton = () => {
    if (isLoadingReview) {
      return <Button loading>Loading Review Status</Button>;
    }
    console.log("checkout date" + checkoutDate);
    console.log("new date" + new Date());
    if (new Date(checkoutDate) <= new Date()) {
      return (
        <Button
          type="primary"
          disabled={reviewExists}
          onClick={() => navigate(`/add-review/${bookingId}`)}
          style={{
            background: reviewExists ? "grey" : "green",
            marginTop: "10px",
          }}
        >
          {reviewExists ? "Review Already Added" : "Add Review"}
        </Button>
      );
    }
    return null;
  };

  const renderTourReviewButton = () => {
    if (isLoadingTourReview) {
      return <Button loading>Loading Review Status</Button>;
    }

    if (new Date(bookingDate) <= new Date()) {
      return (
        <Button
          type="primary"
          disabled={tourReviewExists}
          onClick={() => navigate(`/add-tour-review/${bookingId}`)}
          style={{
            background: tourReviewExists ? "grey" : "green",
            marginTop: "10px",
          }}
        >
          {tourReviewExists ? "Review Already Added" : "Add Tour Review"}
        </Button>
      );
    }
    return null;
  };

  const renderMessageContent = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const lines = text.split("\n");

    return lines.map((line, lineIdx) => {
      const parts = line.split(urlRegex);
      return (
        <div key={lineIdx} style={{ minHeight: "1.2em", lineHeight: "1.5" }}>
          {parts.map((part, partIdx) => {
            if (part && part.match(urlRegex)) {
              return (
                <a
                  key={partIdx}
                  href={part}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#2563eb",
                    textDecoration: "underline",
                    wordBreak: "break-all",
                    fontWeight: "600",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {part}
                </a>
              );
            }
            return part;
          })}
        </div>
      );
    });
  };

  return (
    <div className="chat-wrapper">
      {/* Booking Header */}
      <div className="booking-header">
        <div className="booking-left">
          <button className="back-btn" onClick={() => navigate(-1)}>
            ←
          </button>
          <div className="booking-info">
            <h3 className="booking-title">{contextTitle || "Booking Chat"}</h3>
            <p className="booking-meta">
              Participant: {participant?.full_name || "Unknown"}
            </p>
          </div>
        </div>
        <div className="booking-right">
          {contextType === "booking" && renderHotelReviewButton()}
          {contextType === "tour_booking" && renderTourReviewButton()}
        </div>
      </div>

      {/* Chat Messages */}
      <div className="chat-container">
        <div className="chat-messages">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`chat-message-row ${
                msg.sender.id === currentUserId ? "chat-sent" : "chat-received"
              }`}
            >
              {msg.sender.id !== currentUserId && (
                <img
                  src={senderImg}
                  alt={participant?.full_name}
                  className="chat-profile-img"
                />
              )}
              <div className="chat-message-bubble">
                <div className="chat-message-header">
                  <span className="chat-sender-name">
                    {msg.sender.full_name}
                  </span>
                  <span className="chat-message-time">
                    {new Date(msg.sent_at).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <div className="chat-message-text">{renderMessageContent(msg.message_text)}</div>
              </div>
              {msg.sender.id === currentUserId && (
                <img src={receiverImg} alt="You" className="chat-profile-img" />
              )}
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="chat-input-row">
          <input
            type="text"
            placeholder={isClosed ? "Chat is closed" : "Type a message..."}
            value={input}
            disabled={isClosed}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
          />
          <button onClick={handleSend} disabled={isClosed}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

export default Chat;
